import { FC, useContext } from 'react';
import { NormalizedGame, GameFilters, GameFilterLabels } from 'types/Games';
import { CircularProgress, Text, Box } from '@chakra-ui/react';
import classNames from 'classnames';
import styles from './GameCard.module.css';
import { OptionalTooltip } from 'components/OptionalTooltip/OptionalTooltip';
import { ICON_BY_NAME } from 'ui/components/Icons';
import { gameDisplay } from 'ui/types/Games';
import Coins from 'ui/assets/Coins';
import { useRouter } from 'next/router';
import { MPThemeContext } from 'providers';
import useManagerView from 'hooks/useManagerView';
import { EmbeddedContext } from 'providers';
const GameImage: FC<{
  coverImageUrl: string;
  statusText?: string;
}> = ({
  coverImageUrl,
  statusText
}) => {
  return <div className={classNames(styles.section, styles.gameImageContainer)} data-sentry-component="GameImage" data-sentry-source-file="GameCard.tsx">
      <Box backgroundImage={`url("${coverImageUrl}")`} backgroundPosition="center" backgroundSize="cover" height="100%" width="100%" className={styles.gameImage} borderRadius="10px 0 0 0" position="relative" data-sentry-element="Box" data-sentry-source-file="GameCard.tsx">
        {statusText && <span className={classNames(styles.liveBadge, {
        [styles.liveBadgeEndingSoon]: statusText === 'Ending Soon'
      })}>
            {statusText}
          </span>}
      </Box>
    </div>;
};
const getStatus = (status: string, percentageTimeElapsed: number) => {
  if (status === GameFilters.Completed) {
    return GameFilterLabels[GameFilters.Completed];
  }
  if (status === GameFilters.NeedsVerification) {
    return GameFilterLabels[GameFilters.NeedsVerification];
  }
  if (percentageTimeElapsed > 95) {
    return 'Ending Soon';
  }
  if (status === GameFilters.Active) {
    return 'Live';
  }
};
const GameDetails: FC<{
  gameType: string;
  name: string;
  description: string;
}> = ({
  gameType,
  name,
  description
}) => {
  const {
    isMobile
  } = useContext(MPThemeContext);
  const displayTooltip = !isMobile && description.length > 64;
  return <div className={classNames(styles.section, styles.gameDetailsContainer)} data-sentry-component="GameDetails" data-sentry-source-file="GameCard.tsx">
        <div className={styles.gameTypeContainer}>
          <span className={styles.gameType}>{gameType.toUpperCase()}</span>
        </div>
        <div className={styles.gameName}>{name}</div>
        <OptionalTooltip content={displayTooltip ? description : ''} data-sentry-element="OptionalTooltip" data-sentry-source-file="GameCard.tsx">
          <div className={styles.gameDescription}>
            <p className={styles.ellipsisMultiline}>{description}</p>
          </div>
        </OptionalTooltip>
      </div>;
};
interface GameInfoProps {
  percentageTimeElapsed: number;
  progressReason: string;
  userCount?: number;
  timeRemaining: string;
}
const GameInfo: FC<GameInfoProps> = ({
  percentageTimeElapsed,
  progressReason,
  userCount,
  timeRemaining
}) => {
  return <div className={styles.section} data-sentry-component="GameInfo" data-sentry-source-file="GameCard.tsx">
      <div className={styles.timeRemainingRow}>
        <OptionalTooltip content={progressReason} data-sentry-element="OptionalTooltip" data-sentry-source-file="GameCard.tsx">
          <CircularProgress value={percentageTimeElapsed} max={100} color="venasaur.500" trackColor="#00000042" size="24px" data-sentry-element="CircularProgress" data-sentry-source-file="GameCard.tsx" />
        </OptionalTooltip>
        <Text fontSize="xs" data-sentry-element="Text" data-sentry-source-file="GameCard.tsx">{timeRemaining} Remaining</Text>
      </div>
      <div className={styles.playerCountRow}>
        <ICON_BY_NAME.users color="currentColor" size="16px" data-sentry-element="unknown" data-sentry-source-file="GameCard.tsx" />
        <Text fontSize="xs" data-sentry-element="Text" data-sentry-source-file="GameCard.tsx">{userCount ?? 0} Learners in the game</Text>
      </div>
    </div>;
};
interface GameRewardsProps {
  topPrize?: string | number;
  totalWon: number;
}
const GameRewards: FC<GameRewardsProps> = ({
  topPrize,
  totalWon
}) => {
  return <div className={classNames(styles.section, styles.gameRewardsContainer)} data-sentry-component="GameRewards" data-sentry-source-file="GameCard.tsx">
      <div className={styles.rewardsRow}>
        <span className={styles.rewardsLabel}>Top Prize</span>
        <span className={styles.rewardsValue}>
          <Coins size="16px" data-sentry-element="Coins" data-sentry-source-file="GameCard.tsx" />
          {topPrize ?? '-'}
        </span>
      </div>
      <div className={styles.rewardsRow}>
        <span className={styles.rewardsLabel}>Total Won</span>
        <span className={styles.rewardsValue}>
          <Coins size="16px" data-sentry-element="Coins" data-sentry-source-file="GameCard.tsx" />
          {totalWon}
        </span>
      </div>
    </div>;
};
export const GameCard: FC<{
  game: NormalizedGame;
  managerView: boolean;
}> = ({
  game,
  managerView
}) => {
  const router = useRouter();
  const {
    fulfilledPayout,
    gameType,
    howToWin,
    name,
    pendingPayout,
    percentageTimeElapsed,
    progressReason,
    rewards,
    status,
    timeRemaining,
    userCount
  } = game;
  // Only managers may read manager_view, and only for a game that has one.
  const isManagerView = managerView && game.hasManagerView;
  const {
    managerViewData
  } = useManagerView(isManagerView ? game.id : undefined);
  const topPrize = rewards && rewards.length > 0 ? rewards[0].value : undefined;
  const {
    sendMessage
  } = useContext(EmbeddedContext);
  const totalWon = isManagerView ? managerViewData?.totalPayout ?? 0 : (fulfilledPayout ?? 0) + (pendingPayout ?? 0);
  const statusText = getStatus(status, percentageTimeElapsed);
  const handleGameClick = () => {
    sendMessage(JSON.stringify({
      type: 'UPDUO_FULLSCREEN'
    }));
    router.push(`/games/${game.id}${isManagerView ? '?isManagerView=true' : ''}`);
  };
  return <div className={styles.container} onClick={handleGameClick} data-sentry-component="GameCard" data-sentry-source-file="GameCard.tsx">
      <div className={styles.topContainer}>
        {/* Top left: Image */}
        <GameImage coverImageUrl={game.coverImageUrl || gameDisplay[gameType].image} statusText={statusText} data-sentry-element="GameImage" data-sentry-source-file="GameCard.tsx" />
        {/* Top right: Game Details */}
        <GameDetails gameType={gameType || ''} name={name} description={howToWin} data-sentry-element="GameDetails" data-sentry-source-file="GameCard.tsx" />
      </div>
      <div className={styles.bottomContainer}>
        {/* Bottom left: Game Info */}
        <GameInfo percentageTimeElapsed={percentageTimeElapsed} progressReason={progressReason} userCount={userCount} timeRemaining={timeRemaining} data-sentry-element="GameInfo" data-sentry-source-file="GameCard.tsx" />
        {/* Bottom right: Game Rewards */}
        <GameRewards topPrize={topPrize} totalWon={totalWon} data-sentry-element="GameRewards" data-sentry-source-file="GameCard.tsx" />
      </div>
    </div>;
};